// src/components/home/title-vendor-picker.jsx
// One side's title picker on the turn-in: choose a preferred vendor, add a new
// one to the shared list, or fill a one-time entry (this deal only). Value shape:
// { company, officerName, email, phone }.
import { useState } from "react";
import { ArrowLeft, ChevronDown, Pencil, Plus, Save } from "lucide-react";
import { newVendorId } from "../../utils/titleVendors";

const inputCls =
  "w-full border border-slate-300 rounded-lg p-2 text-sm focus:border-[#1E293B] outline-none";

export default function TitleVendorPicker({
  label,
  tag,
  vendors = [],
  value,
  onChange,
  onVendorAdded,
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("select"); // "select" | "custom"
  const [savable, setSavable] = useState(false); // custom came from "Add new"

  const v = value || {};
  const selectedLabel = v.company
    ? `${v.company}${v.officerName ? " — " + v.officerName : ""}`
    : "Choose a title vendor";

  const pickVendor = (vendor) => {
    onChange({
      company: vendor.company || "",
      officerName: vendor.officerName || "",
      email: vendor.email || "",
      phone: vendor.phone || "",
    });
    setOpen(false);
    setMode("select");
  };

  const startCustom = (canSave) => {
    onChange({ company: "", officerName: "", email: "", phone: "" });
    setSavable(canSave);
    setMode("custom");
    setOpen(false);
  };

  const set = (key, val) => onChange({ ...v, [key]: val });

  const saveToList = async () => {
    if (!(v.company || "").trim()) return;
    await onVendorAdded?.({
      id: newVendorId(),
      company: v.company,
      officerName: v.officerName,
      email: v.email,
      phone: v.phone,
    });
    setSavable(false);
  };

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 mb-1">
        <label className="block font-medium text-sm">{label}</label>
        {tag && (
          <span className="text-[11px] font-medium bg-slate-100 text-linkColor px-2 py-0.5 rounded-full">
            {tag}
          </span>
        )}
      </div>

      {mode === "select" ? (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="w-full flex items-center justify-between border border-slate-300 rounded-lg p-2 text-sm focus:border-[#1E293B] outline-none"
          >
            <span className={v.company ? "text-slate-800" : "text-slate-400"}>
              {selectedLabel}
            </span>
            <ChevronDown size={18} className="text-slate-400" />
          </button>

          {open && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setOpen(false)}
              />
              <div className="absolute left-0 right-0 mt-1 z-20 bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 max-h-64 overflow-auto">
                {vendors.length === 0 && (
                  <div className="px-3 py-2 text-sm text-slate-400">
                    No saved vendors yet
                  </div>
                )}
                {vendors.map((vend) => (
                  <button
                    key={vend.id}
                    type="button"
                    onClick={() => pickVendor(vend)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-slate-800"
                  >
                    {vend.company}
                    {vend.officerName ? ` — ${vend.officerName}` : ""}
                  </button>
                ))}
                <div className="h-px bg-slate-100 my-1 mx-2" />
                {onVendorAdded && (
                  <button
                    type="button"
                    onClick={() => startCustom(true)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm font-medium text-[#1E293B] flex items-center gap-2"
                  >
                    <Plus size={15} /> Add a new preferred vendor
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => startCustom(false)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-slate-600 flex items-center gap-2"
                >
                  <Pencil size={14} /> One-time use (this deal only)
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              className={inputCls}
              placeholder="Title company"
              value={v.company || ""}
              onChange={(e) => set("company", e.target.value)}
            />
            <input
              className={inputCls}
              placeholder="Title officer"
              value={v.officerName || ""}
              onChange={(e) => set("officerName", e.target.value)}
            />
            <input
              className={inputCls}
              placeholder="Officer email"
              value={v.email || ""}
              onChange={(e) => set("email", e.target.value)}
            />
            <input
              className={inputCls}
              placeholder="Officer phone"
              value={v.phone || ""}
              onChange={(e) => set("phone", e.target.value)}
            />
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setMode("select")}
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
            >
              <ArrowLeft size={14} /> Back to list
            </button>
            {savable ? (
              <button
                type="button"
                onClick={saveToList}
                className="text-sm text-[#1E293B] font-medium hover:underline flex items-center gap-1"
              >
                <Save size={14} /> Save to team list
              </button>
            ) : (
              <span className="text-xs text-slate-400">
                One-time — not saved to your list
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
